import "./CalendarView.css";
import cn from "classnames";
import { type ComponentProps, useCallback, useMemo } from "react";
import {
	getBlogPlatforms,
	getSocialPlatforms,
	Platform,
	type PlatformValue,
} from "shared";
import { type Icon, IconSVG } from "../common/Icon";
import { SimpleTooltip } from "../common/SimpleTooltip";
import { ButtonIcon } from "../components/ButtonIcon";
import { useFeatureFlag } from "../hooks/use-app-metadata";
import { type CalendarType, useCalendar } from "./CalendarContext";

const PLATFORM_ICONS: Record<
	PlatformValue,
	ComponentProps<typeof Icon>["name"]
> = {
	[Platform.FACEBOOK]: IconSVG.Facebook,
	[Platform.INSTAGRAM]: IconSVG.Instagram,
	[Platform.THREADS]: IconSVG.BrandThreads,
	[Platform.LINKEDIN]: IconSVG.LinkedIn,
	[Platform.GOOGLE_BUSINESS]: IconSVG.Google,
	[Platform.YOUTUBE]: IconSVG.YouTube,
	[Platform.TIKTOK]: IconSVG.TikTok,
	[Platform.X]: IconSVG.BrandX,
	[Platform.WORDPRESS]: IconSVG.Wordpress,
	[Platform.CUSTOM_BLOG]: "text_ad",
};

export const CalendarView = () => {
	const {
		view,
		type: calendarType,
		handleNext,
		handlePrev,
		handleCalendarView,
		handleListView,
		handleCalendarType,
		label,
		platformFilter,
		togglePlatformFilter,
		clearPlatformFilter,
	} = useCalendar();

	const isYouTubeEnabled = useFeatureFlag("YouTubeIntegration");
	const isTikTokEnabled = useFeatureFlag("TikTokIntegration");
	const isXEnabled = useFeatureFlag("XIntegration");

	const filterPlatforms = useMemo(() => {
		return [...getSocialPlatforms(), ...getBlogPlatforms()].filter(
			(platform) => {
				if (platform === Platform.YOUTUBE && !isYouTubeEnabled) {
					return false;
				}
				if (platform === Platform.TIKTOK && !isTikTokEnabled) {
					return false;
				}
				if (platform === Platform.X && !isXEnabled) {
					return false;
				}
				return true;
			},
		);
	}, [isYouTubeEnabled, isTikTokEnabled, isXEnabled]);

	const handleChangeType = useCallback(
		(type: CalendarType) => {
			handleCalendarType(type);
		},
		[handleCalendarType],
	);

	const showAllActive = platformFilter === undefined;

	return (
		<div className="calendar-controls">
			<div className="calendar-controls-month">
				<ButtonIcon
					aria-label="previous"
					icon="chevron_left"
					onClick={handlePrev}
				/>
				<ButtonIcon
					aria-label="next"
					icon="chevron_right"
					onClick={handleNext}
				/>
				<time>{label}</time>
			</div>

			{view === "calendar" && (
				<div className="calendar-type">
					<ul>
						<li>
							<button
								type="button"
								className={calendarType === "month" ? "active" : ""}
								onClick={() => handleChangeType("month")}
							>
								Month
							</button>
						</li>

						<li>
							<button
								type="button"
								className={calendarType === "week" ? "active" : ""}
								onClick={() => handleChangeType("week")}
							>
								Week
							</button>
						</li>
					</ul>
				</div>
			)}

			<fieldset className="calendar-platform-filter">
				<legend className="visually-hidden">Filter by network</legend>
				<button
					type="button"
					className={cn("calendar-platform-filter-all", {
						active: showAllActive,
					})}
					onClick={clearPlatformFilter}
				>
					All
				</button>
				{filterPlatforms.map((platform) => {
					const active = showAllActive || platformFilter?.has(platform);
					return (
						<SimpleTooltip key={platform} tooltip={platform} placement="top">
							<ButtonIcon
								icon={PLATFORM_ICONS[platform]}
								aria-label={`Filter ${platform}`}
								aria-pressed={Boolean(platformFilter?.has(platform))}
								onClick={() => togglePlatformFilter(platform)}
								className={cn("button-icon calendar-platform-filter-btn", {
									active,
								})}
							/>
						</SimpleTooltip>
					);
				})}
			</fieldset>

			<div className="calendar-controls-view">
				<SimpleTooltip tooltip="Calendar view" placement="top">
					<ButtonIcon onClick={handleCalendarView} icon="view_comfy_alt" />
				</SimpleTooltip>
				<SimpleTooltip tooltip="List view" placement="top">
					<ButtonIcon onClick={handleListView} icon="reorder" />
				</SimpleTooltip>
			</div>
		</div>
	);
};
